import React, { useState } from 'react';
import { FaUpload, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { uploadImageToCode } from '../services/codeDataStore';

/**
 * Image URL input with an "Upload Image" button. Uploading saves the file to Firebase Storage
 * and fills the input with its URL.
 */
const ImageUploadField = ({ label, value, onChange, placeholder = 'https://.../image.jpg or /assets/img/...', name }) => {
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // { ok: boolean, text: string }

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setStatus({ ok: false, text: 'Please choose an image file.' });
      return;
    }

    setUploading(true);
    setStatus(null);
    try {
      const url = await uploadImageToCode(file);
      onChange(url);
      setStatus({
        ok: true,
        text: 'Uploaded. Save the form to keep it.',
      });
    } catch (err) {
      setStatus({ ok: false, text: err.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">{label}</label>
        <label
          className={`inline-flex items-center gap-2 px-3.5 py-1.5 bg-[#03478E] hover:bg-[#023366] text-white text-xs font-semibold rounded-full cursor-pointer shadow-xs transition ${
            uploading ? 'opacity-60 pointer-events-none' : ''
          }`}
        >
          <FaUpload className="text-[10px]" />
          <span>{uploading ? 'Uploading...' : 'Upload Image'}</span>
          <input type="file" accept="image/*" onChange={handleFile} className="hidden" />
        </label>
      </div>
      <input
        type="text"
        name={name}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
      />
      {status && (
        <div className={`flex items-start gap-1.5 text-xs font-medium ${status.ok ? 'text-blue-700' : 'text-red-600'}`}>
          {status.ok ? (
            <FaCheckCircle className="text-green-600 text-xs flex-shrink-0 mt-0.5" />
          ) : (
            <FaExclamationCircle className="text-xs flex-shrink-0 mt-0.5" />
          )}
          <span>{status.text}</span>
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
